import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenIcon, HelpCircleIcon, MailIcon, MessageSquareTextIcon, PlayCircleIcon } from 'lucide-react';
import { CaseSlideShell } from './CaseSlideShell';
import { nordicAudiences, nordicCampaign } from '../../data/nordicCase';
const JOURNEY = [{
  label: 'Email',
  icon: MailIcon
}, {
  label: 'Clinical question',
  icon: HelpCircleIcon
}, {
  label: 'Interactive Snak™',
  icon: PlayCircleIcon
}, {
  label: 'HCP response',
  icon: MessageSquareTextIcon
}, {
  label: 'Relevant resources',
  icon: BookOpenIcon
}] as const;
export function CaseExperiment() {
  const {
    target,
    nonTarget
  } = nordicAudiences;
  const total = target.value + nonTarget.value;
  return <CaseSlideShell step="02" eyebrow="The Denmark experiment" surface headline={<>
          Could digital interaction{' '}
          <span className="text-[var(--brand)]" data-id="element-213" data-mp="9lm51w9gmhqi">extend BI’s reach?</span>
        </>} intro={`BI Denmark used a patient-focused disease-awareness campaign on type 2 diabetes and cardiovascular comorbidity to engage ${nordicCampaign.audience} — useful, not promotional.`} data-id="element-212" data-mp="124134qymdb2o">
      <ol className="flex flex-shrink-0 items-stretch gap-[clamp(0.3rem,0.8vw,0.75rem)]" data-id="element-214" data-mp="1lc7xwqfms1f8">
        {JOURNEY.map((step, index) => <React.Fragment key={step.label} data-id="element-215" data-mp="1ha35tw4tzcup" data-mpi={index}>
            <motion.li initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.08,
          duration: 0.4
        }} className="flex min-w-0 flex-1 flex-col items-center gap-2 rounded-2xl bg-white px-2 py-[clamp(0.7rem,1.3vw,1.2rem)] text-center shadow-sm ring-1 ring-black/5" data-id="element-216" data-mp="1h91rnisiqa8r">
              <span className="flex h-[clamp(2.2rem,3vw,3rem)] w-[clamp(2.2rem,3vw,3rem)] items-center justify-center rounded-xl bg-[var(--brand)]" data-id="element-217" data-mp="ydtjymnoti05">
                <step.icon className="h-[clamp(1.05rem,1.4vw,1.4rem)] w-[clamp(1.05rem,1.4vw,1.4rem)] text-white" aria-hidden="true" data-id="element-218" data-mp="2spi2szx1i4pt" />
              </span>
              <span className="text-[clamp(0.75rem,1.1vw,1.05rem)] font-bold leading-tight text-[var(--fg)]" data-id="element-219" data-mp="q21ig120fzmu">
                {step.label}
              </span>
            </motion.li>
            {index < JOURNEY.length - 1 && <li className="flex flex-shrink-0 items-center" aria-hidden="true" data-id="element-220" data-mp="l403nsbmamh6">
                <ArrowRightIcon className="h-4 w-4 text-[var(--brand)]" data-id="element-221" data-mp="l1x1yb13r4wm" />
              </li>}
          </React.Fragment>)}
      </ol>

      <div className="mt-[clamp(1rem,2vw,1.75rem)] flex min-h-0 flex-1 flex-col" data-id="element-222" data-mp="1drvy0qdps2as">
        <p className="mb-[clamp(0.4rem,0.8vw,0.7rem)] text-[clamp(0.6rem,0.85vw,0.78rem)] font-bold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-223" data-mp="ztyybl3yrfrw">
          Two campaign audiences · emailed {nordicCampaign.period}
        </p>
        <div className="flex min-h-0 flex-1 gap-[clamp(0.5rem,1vw,0.9rem)]" data-id="element-224" data-mp="3khtpwd5xaxdc">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.6,
          duration: 0.5
        }} style={{
          flexGrow: target.value / total
        }} className="flex min-w-0 basis-0 flex-col justify-end rounded-2xl border-2 border-dashed border-[var(--fg)]/15 bg-white/60 p-[clamp(0.9rem,1.6vw,1.5rem)]" data-id="element-225" data-mp="3pirg9d136xpx">
            <p className="text-[clamp(2rem,4.2vw,4rem)] font-extrabold leading-none tracking-[-0.05em] text-[var(--muted)]" data-id="element-226" data-mp="3m0afqbxjj1wm">
              {target.recipients}
            </p>
            <p className="mt-1 text-[clamp(0.85rem,1.25vw,1.2rem)] font-bold text-[var(--muted)]" data-id="element-227" data-mp="2tc6cry6ct2xm">
              {target.label}
            </p>
            <p className="text-[clamp(0.7rem,0.95vw,0.9rem)] text-[var(--muted)]" data-id="element-228" data-mp="382dkbiutp1jj">
              Already within field-force focus
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.7,
          duration: 0.5
        }} style={{
          flexGrow: nonTarget.value / total
        }} className="relative flex min-w-0 basis-0 flex-col justify-end rounded-2xl bg-[var(--brand)] p-[clamp(0.9rem,1.6vw,1.5rem)] shadow-[0_20px_50px_rgba(0,84,61,0.25)]" data-id="element-229" data-mp="2fjykaim3ry4q">
            <span className="absolute right-[clamp(0.9rem,1.6vw,1.5rem)] top-[clamp(0.9rem,1.6vw,1.5rem)] rounded-full bg-white px-3 py-1 text-[clamp(0.58rem,0.8vw,0.74rem)] font-bold uppercase tracking-[0.16em] text-[var(--brand)]" data-id="element-230" data-mp="1mtnc7rj1axml">
              The strategic story
            </span>
            <p className="text-[clamp(2.8rem,6vw,5.75rem)] font-extrabold leading-none tracking-[-0.05em] text-white" data-id="element-231" data-mp="21j2wqprz26tu">
              {nonTarget.recipients}
            </p>
            <p className="mt-1 text-[clamp(0.95rem,1.45vw,1.4rem)] font-bold text-white" data-id="element-232" data-mp="2akakwf0rxra7">
              {nonTarget.label}
            </p>
            <p className="text-[clamp(0.72rem,1vw,0.95rem)] text-[var(--brand-lighter)]" data-id="element-233" data-mp="c5ps0zfy6gs7">
              Not receiving BI sales calls
            </p>
          </motion.div>
        </div>
      </div>
    </CaseSlideShell>;
}